import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Form, Input, Space, Typography, message } from 'antd';
import classNames from 'classnames/bind';

import LOGO from '../../assets/common/logo.png';
import PublicAPI from '../../helpers/PublicAPI';
import {
  PASSWORD_PATTERN,
  PASSWORD_RULE_MESSAGE,
  PASSWORD_SPECIALS,
} from '../../constants/passwordPolicy';
import { getApiErrorMessage } from '../../utils/getApiErrorMessage';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const watchedValues = Form.useWatch([], form);
  const [isSaving, setIsSaving] = useState(false);
  const token = searchParams.get('token')?.trim() || '';

  const tokenExists = useMemo(() => token.length > 0, [token]);

  const doResetPassword = async (values) => {
    if (!tokenExists) {
      message.error('유효하지 않은 비밀번호 재설정 링크입니다.');
      return;
    }

    setIsSaving(true);
    const response = await PublicAPI.post('/password-reset/confirm', {
      token,
      newPassword: values.password,
    });
    setIsSaving(false);

    if (!response?.success) {
      message.error(
        getApiErrorMessage(response, '유효하지 않거나 만료된 비밀번호 재설정 링크입니다.'),
      );
      return;
    }

    message.success('비밀번호가 재설정되었습니다. 다시 로그인해주세요.');
    navigate('/login', { replace: true });
  };

  return (
    <div className={cx('container')}>
      <Form form={form} layout="vertical" onFinish={doResetPassword}>
        <div className={cx('box')}>
          <img className={cx('logo')} src={LOGO} alt="BTE 비밀번호 재설정" />
          <Space direction="vertical" className={cx('fieldsWrapper')} size={16}>
            <Typography.Title level={4} className={cx('title')}>
              비밀번호 재설정
            </Typography.Title>
            <Typography.Text className={cx('description')}>
              새 비밀번호를 입력하고 저장해주세요.
            </Typography.Text>
            {!tokenExists && (
              <Typography.Text type="danger" className={cx('description')}>
                유효하지 않은 비밀번호 재설정 링크입니다.
              </Typography.Text>
            )}
            <Form.Item
              name="password"
              label="새 비밀번호"
              rules={[
                { required: true, message: '새 비밀번호를 입력해주세요.' },
                {
                  validator: (_, value) =>
                    !value || PASSWORD_PATTERN.test(value)
                      ? Promise.resolve()
                      : Promise.reject(new Error(PASSWORD_RULE_MESSAGE)),
                },
              ]}>
              <Input.Password maxLength={20} size="large" />
            </Form.Item>
            <Form.Item
              name="checkPassword"
              label="새 비밀번호 확인"
              dependencies={['password']}
              rules={[
                { required: true, message: '새 비밀번호 확인을 입력해주세요.' },
                {
                  validator: (_, value) =>
                    watchedValues?.password === value
                      ? Promise.resolve()
                      : Promise.reject(new Error('입력된 비밀번호와 다릅니다.')),
                },
              ]}>
              <Input.Password maxLength={20} size="large" />
            </Form.Item>
            <Typography.Text type="secondary" className={cx('passwordHint')}>
              허용 특수문자: {PASSWORD_SPECIALS}
            </Typography.Text>
            <Button
              htmlType="submit"
              type="primary"
              block
              size="large"
              loading={isSaving}
              disabled={isSaving || !tokenExists}>
              저장하기
            </Button>
            <Button block size="large" onClick={() => navigate('/login')}>
              로그인으로 돌아가기
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
};

export default ResetPasswordPage;
